import type { TableProps } from "../../utils/local-antd";
import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";
import React, { useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { Flex } from "../../components/Flex";
import { AtiraTable } from "../../components/table/AtiraTable";
import { PageMeta } from "../../model/meta/PageMeta";

import { UserRoles } from "../../model/user/types/UserRoles";
import { permissionSliceSelectors } from "../../redux/permission/permission.selector";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { taskSliceSelectors } from "../../redux/task/task.selector";
import { taskActions } from "../../redux/task/task.slice";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { Spacing } from "../../theme/Spacing";
import { createTasksColumns } from "./columns/createTasksTablesColumns";
import { TaskTableHeader } from "./components/TasksTableHeader";
import { useDrawersContext } from "./drawers-context";
import { TaskKind } from "../../model/task/shared/TasksKind.enum";
import { GetTasksFilterDto } from "../../model/task/dto/GetTasksFilterDto";
import { BaseTask } from "../../model/task/Task";
import { KanbanCard } from "../../model/kanban/KanbanCard";
import { Entry } from "../../model/entry/Entry";

const TableWrapper = styled(Flex)`
  width: 100%;
  flex-direction: column;
  padding: 0 ${Spacing.m};

  .contact-task .actionable-cell {
    button {
      color: ${({ theme }) => theme.onContactTask} !important;
    }

    button p {
      color: inherit;
    }

    svg {
      color: ${({ theme }) => theme.contactTask} !important;
    }
  }

  .kanban-task .actionable-cell {
    button {
      color: ${({ theme }) => theme.onKanbanTask} !important;
    }

    button p {
      color: inherit;
    }

    svg {
      color: ${({ theme }) => theme.kanbanTask} !important;
    }
  }

  .lead-task .actionable-cell {
    button {
      color: ${({ theme }) => theme.onLeadTask} !important;
    }

    button p {
      color: inherit;
    }

    svg {
      color: ${({ theme }) => theme.leadTask} !important;
    }
  }

  .orphan-task .actionable-cell p,
  .contact-task .actionable-cell p,
  .kanban-task .actionable-cell p,
  .lead-task .actionable-cell p {
    color: black !important;
  }

  .orphan-task .actionable-cell {
    button {
      color: ${({ theme }) => theme.onOrphanTask} !important;
    }

    button p {
      color: inherit;
    }
  }
`;

export const TasksTable: React.FC = () => {
  const allTasks = useAppSelector(taskSliceSelectors.selectAllTasks);
  const allTasksMeta = useAppSelector(taskSliceSelectors.selectAllTasksMeta);
  const tasksPage = useAppSelector(taskSliceSelectors.selectTasksPage);
  const tasksPageSize = useAppSelector(taskSliceSelectors.selectTasksPageSize);
  const loading = useAppSelector(taskSliceSelectors.selectAllTasksLoading);
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const members = useAppSelector(permissionSliceSelectors.selectMembers);

  const location = useLocation();
  const { getValues, setValue } = useFormContext<GetTasksFilterDto>();

  const metaState = location.state?.meta;

  const userPermissions = user.permissions.permissions;

  const canAssign = user.roles.includes(UserRoles.ORGANIZATION);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    setTask,
    setTaskDeleteVisible,
    setEntry,
    setKanbanCard,

    setTaskReadVisible,
    setTaskUpdateVisible,

    setTaskAssignVisible,

    setTaskDoneVisible,

    setEntryDetailsVisible,
    setKanbanCardReadVisible,
  } = useDrawersContext();

  const onPreviewRelatedTo = useCallback(
    (task: BaseTask) => {
      if (!task?.parent) {
        return;
      }

      setTask(task);

      if (task?.kind?.toLowerCase() === TaskKind.KANBAN.toLowerCase()) {
        setKanbanCard(task.parent as KanbanCard);
        setKanbanCardReadVisible(true);
      } else {
        setEntry(task.parent as Entry);
        setEntryDetailsVisible(true);
      }
    },
    [
      setEntryDetailsVisible,
      setKanbanCardReadVisible,
      setEntry,
      setKanbanCard,
      setTask,
    ],
  );

  const onUpdateTask = useCallback(
    (task: BaseTask) => {
      setTask(task);
      setTaskUpdateVisible(true);
    },
    [setTaskUpdateVisible, setTask],
  );

  const onPreviewTask = useCallback(
    (task: BaseTask) => {
      setTask(task);
      setTaskReadVisible(true);
    },
    [setTaskReadVisible, setTask],
  );

  const onAssignTask = useCallback(
    (task: BaseTask) => {
      setTask(task);
      setTaskAssignVisible(true);
    },
    [setTask, setTaskAssignVisible],
  );

  const onDeleteTask = useCallback(
    (task: BaseTask) => {
      setTask(task);
      setTaskDeleteVisible(true);
    },
    [setTaskDeleteVisible, setTask],
  );

  const onToggleDone = useCallback(
    (task: BaseTask) => {
      setTask(task);
      setTaskDoneVisible(true);
    },
    [setTask, setTaskDoneVisible],
  );

  const columns = useMemo(
    () =>
      createTasksColumns(
        TaskKind.LEAD,
        {
          deleteTask: onDeleteTask,
          previewTask: onPreviewTask,
          updateTask: onUpdateTask,
          assign: canAssign ? onAssignTask : undefined,
          previewRelatedTo: onPreviewRelatedTo,
          toggleDone: onToggleDone,
          t,
        },
        userPermissions,
        user,
        members,
      ) as TableProps<BaseTask>["columns"],
    [
      onDeleteTask,
      onPreviewTask,
      onUpdateTask,
      canAssign,
      onAssignTask,
      onPreviewRelatedTo,
      onToggleDone,
      t,
      userPermissions,
      user,
      members,
    ],
  );

  const onPageChange = async (page: number, pageSize: number) => {
    dispatch(taskActions.setTasksTablePage(page));
    dispatch(taskActions.setTasksTablePageSize(pageSize));
  };

  const onTableShowSize = (curr: number, newSize: number) => {
    dispatch(taskActions.setTasksTablePageSize(newSize));
    onPageChange(1, newSize);
  };

  useEffect(() => {
    if (location.state?.useFilter) {
      const { range, scope, done, reminder } = location.state.useFilter;

      setValue("range", range ?? undefined);
      setValue("scope", scope ?? undefined);
      setValue("done", done ?? null);
      setValue("reminder", reminder ?? undefined);
      setValue("kind", undefined);
      setValue("keyword", "");

      window.history.replaceState(
        { ...location.state, useFilter: undefined },
        "",
      );
    }

    dispatch(
      taskActions.getAllTasks({
        userId: user._id,
        meta:
          metaState ||
          PageMeta.create({
            page: tasksPage - 1,
            count: tasksPageSize,
          }),
        ...omitBy(getValues(), isNil),
      }),
    );
  }, [
    dispatch,
    getValues,
    location.state,
    metaState,
    setValue,
    tasksPage,
    tasksPageSize,
    user._id,
  ]);

  return (
    <TableWrapper>
      <TaskTableHeader showFilters />

      <AtiraTable
        rowKey={(x) => x._id}
        data={allTasks}
        columns={columns}
        loading={loading}
        pagination={{
          pageSizeOptions: ["5", "10", "20", "50"],
          pageSize: tasksPageSize,
          current: tasksPage,
          total: allTasksMeta?.total,
          onChange: onPageChange,
          showSizeChanger: true,
          onShowSizeChange: onTableShowSize,
          placement: ["bottomCenter"],
        }}
        rowSelection={undefined}
      />
    </TableWrapper>
  );
};
